import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, LoadScript } from '@react-google-maps/api';
import env from 'lib/env';
import Search from 'models/search';
import { Location } from 'models/locations';

const { GOOGLE_MAPS_API_KEY } = env();
const DEFAULT_INITIAL_ZOOM = 15;

export class GoogleMapView extends Component {
  constructor(props) {
    super(props);
    this.map = null;
    this.triggerBoundsChange = this.triggerBoundsChange.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!this.map) return;

    const { currentSearch, selectedLocation } = this.props;
    const prevSearch = prevProps.currentSearch;

    // Detect if search coordinate changed programmatically
    const currentLat = currentSearch.lat || currentSearch.anchorLat;
    const currentLng = currentSearch.lng || currentSearch.anchorLng;
    const prevLat = prevSearch.lat || prevSearch.anchorLat;
    const prevLng = prevSearch.lng || prevSearch.anchorLng;

    const destinationChanged = currentSearch.destination !== prevSearch.destination ||
                               currentLat !== prevLat ||
                               currentLng !== prevLng;

    if (destinationChanged && !selectedLocation) {
      this.map.setCenter({ lat: currentLat, lng: currentLng });
      if (currentSearch.zoomLevel) {
        this.map.setZoom(currentSearch.zoomLevel);
      }
    }

    // Handle fitBounds for selectedLocation
    if (selectedLocation && selectedLocation !== prevProps.selectedLocation) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: currentLat, lng: currentLng });
      if (selectedLocation.entrances && selectedLocation.entrances.size > 0) {
        const firstEntrance = selectedLocation.entrances.first();
        bounds.extend({
          lat: firstEntrance.lat,
          lng: firstEntrance.lng,
        });
      }
      this.map.fitBounds(bounds, {
        top: 100,
        bottom: 25,
        left: 50,
        right: 100,
      });
    }
  }

  triggerBoundsChange() {
    if (!this.map || this.props.selectedLocation) return;

    const { currentSearch, boundsChange } = this.props;
    const bounds = this.map.getBounds();
    const center = this.map.getCenter();
    const zoomLevel = this.map.getZoom();

    if (!bounds || !center) return;

    const lat = center.lat();
    const lng = center.lng();

    // Avoid infinite loops by skipping updates if coordinates are unchanged
    const searchLat = currentSearch.lat || currentSearch.anchorLat;
    const searchLng = currentSearch.lng || currentSearch.anchorLng;
    const searchZoom = currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM;

    const latDiff = Math.abs(lat - searchLat);
    const lngDiff = Math.abs(lng - searchLng);
    const zoomDiff = Math.abs(zoomLevel - searchZoom);

    if (latDiff < 0.0001 && lngDiff < 0.0001 && zoomDiff < 0.1) {
      return;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const mockBounds = {
      getNorthEast: () => ({ lat: ne.lat(), lng: ne.lng() }),
      getSouthWest: () => ({ lat: sw.lat(), lng: sw.lng() }),
    };

    boundsChange({
      bounds: mockBounds,
      lat,
      lng,
      zoomLevel,
      router: false,
    });
  }

  render() {
    const { currentSearch } = this.props;
    const lat = currentSearch.lat || currentSearch.anchorLat;
    const lng = currentSearch.lng || currentSearch.anchorLng;
    const zoom = currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM;

    return (
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%' }}
          center={{ lat, lng }}
          zoom={zoom}
          onLoad={(map) => { this.map = map; }}
          onDragEnd={() => this.triggerBoundsChange()}
          onZoomChanged={() => this.triggerBoundsChange()}
          options={{
            disableDefaultUI: true,
            gestureHandling: 'cooperative',
          }}
        >
          {/* Markers/overlays omitted for Phase 1 */}
        </GoogleMap>
      </LoadScript>
    );
  }
}

GoogleMapView.propTypes = {
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  previousSearch: PropTypes.instanceOf(Search).isRequired,
  selectedLocation: PropTypes.instanceOf(Location),
  locations: PropTypes.any,
  boundsChange: PropTypes.func.isRequired,
};

GoogleMapView.defaultProps = {
  selectedLocation: null,
  locations: null,
};

export default GoogleMapView;
